import { useEffect, useMemo, useState } from 'react'
import { useAppStore } from '../../store/app'
import { NavBar, NavBarButton } from '../../components/NavBar'
import {
  createScheduledTrigger,
  listScheduledTriggerRuns,
  updateScheduledTrigger,
  type ScheduledTriggerInput,
} from '../../api/ws'
import type { ScheduledTriggerRun } from '../../store/types'
import { runStatusLabel, runStatusTone, runRelativeTime } from './runFormat'
import {
  cronToModel,
  cronToText,
  deviceTimezone,
  endOfDayInZone,
  modelToCron,
  parseTimeValue,
  tomorrowInZone,
  toTimeValue,
  zonedDateValue,
  DOW_LABELS,
  type Frequency,
  type ScheduleModel,
} from './schedule'
import './routines.css'

// How a Routine stops. Mirrors calendar recurrence, deliberately: this is the
// vocabulary people already have for "repeats, but not forever".
type EndMode = 'never' | 'date' | 'count'

const END_OPTIONS: Array<{ value: EndMode; label: string }> = [
  { value: 'never', label: 'Never' },
  { value: 'date', label: 'On date' },
  { value: 'count', label: 'After' },
]

const FREQ_OPTIONS: Array<{ value: Frequency; label: string }> = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekdays', label: 'Weekdays' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'hourly', label: 'Hourly' },
  { value: 'advanced', label: 'Advanced' },
]

export function RoutineEditorScreen({ triggerID }: { triggerID?: string }) {
  const popNav = useAppStore((s) => s.popNav)
  const orgID = useAppStore((s) => s.organizationID)
  const existing = useAppStore((s) =>
    triggerID ? s.scheduledTriggers.find((t) => t.id === triggerID) : undefined,
  )
  const fullTimeAgents = useAppStore((s) =>
    s.agentInstances.filter((a) => !a.is_temp && !a.retired_at),
  )

  // Seed the form once from the existing row (or defaults for a new routine).
  const seed = useMemo(() => {
    const model: ScheduleModel = existing
      ? cronToModel(existing.cron_expr)
      : { freq: 'daily', hour: 5, minute: 0 }
    return {
      name: existing?.name ?? '',
      prompt: existing?.prompt ?? '',
      enabled: existing?.enabled ?? true,
      timezone: existing?.timezone ?? deviceTimezone(),
      targetMode: existing?.target_mode ?? ('temp' as 'temp' | 'existing'),
      targetAgentID: existing?.target_ai_agent_instance_id ?? null,
      model,
      endMode: (existing?.max_runs
        ? 'count'
        : existing?.ends_at
          ? 'date'
          : 'never') as EndMode,
      endDate: zonedDateValue(existing?.ends_at ?? '', existing?.timezone ?? deviceTimezone()),
      endCount: existing?.max_runs ?? 10,
    }
  }, [existing])

  const [name, setName] = useState(seed.name)
  const [prompt, setPrompt] = useState(seed.prompt)
  const [enabled, setEnabled] = useState(seed.enabled)
  const [targetMode, setTargetMode] = useState(seed.targetMode)
  const [targetAgentID, setTargetAgentID] = useState<string | null>(seed.targetAgentID)

  const [freq, setFreq] = useState<Frequency>(seed.model.freq)
  const [hour, setHour] = useState('hour' in seed.model ? seed.model.hour : 5)
  const [minute, setMinute] = useState('minute' in seed.model ? seed.model.minute : 0)
  const [dow, setDow] = useState(seed.model.freq === 'weekly' ? seed.model.dow : 1)
  const [advancedCron, setAdvancedCron] = useState(
    seed.model.freq === 'advanced' ? seed.model.cron : existing?.cron_expr ?? '0 5 * * *',
  )

  const [endMode, setEndMode] = useState<EndMode>(seed.endMode)
  const [endDate, setEndDate] = useState(seed.endDate)
  const [endCount, setEndCount] = useState(seed.endCount)

  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Recent run history (edit mode only). Runs aren't pushed, so pull once.
  const [runs, setRuns] = useState<ScheduledTriggerRun[]>([])
  useEffect(() => {
    if (!triggerID) return
    let cancelled = false
    void listScheduledTriggerRuns(triggerID).then((res) => {
      if (!cancelled) setRuns(res.scheduled_trigger_runs ?? [])
    })
    return () => {
      cancelled = true
    }
  }, [triggerID])

  function buildCron(): string {
    switch (freq) {
      case 'daily':
        return modelToCron({ freq: 'daily', hour, minute })
      case 'weekdays':
        return modelToCron({ freq: 'weekdays', hour, minute })
      case 'weekly':
        return modelToCron({ freq: 'weekly', dow, hour, minute })
      case 'hourly':
        return modelToCron({ freq: 'hourly', minute })
      case 'advanced':
        return advancedCron.trim()
    }
  }

  const preview = cronToText(buildCron())

  // Says what will happen, including the part people get wrong: a skipped
  // occurrence (host asleep, previous run still going) does NOT use up a run.
  const runsSoFar = existing?.run_count ?? 0
  const endHint = (() => {
    if (endMode === 'never') return 'Runs until you switch it off.'
    if (endMode === 'date') {
      return endDate
        ? `Stops after the last run on ${endDate}. Included, in ${seed.timezone}.`
        : 'Pick the last day it should run.'
    }
    const remaining = Math.max(0, endCount - runsSoFar)
    const ran = runsSoFar === 1 ? '1 run so far' : `${runsSoFar} runs so far`
    return runsSoFar > 0
      ? `${ran} — ${remaining} to go. Skipped runs don't count.`
      : "Stops after this many runs. Skipped runs don't count."
  })()

  async function save() {
    if (!orgID) return
    if (!name.trim()) return setError('Give the routine a name.')
    if (!prompt.trim()) return setError('Tell the agent what to do.')
    if (targetMode === 'existing' && !targetAgentID) return setError('Pick an agent to run.')
    if (endMode === 'date' && !endDate) return setError('Pick the date it should stop.')
    if (endMode === 'count' && (!Number.isFinite(endCount) || endCount < 1)) {
      return setError('A run limit needs to be at least 1.')
    }

    const input: ScheduledTriggerInput = {
      name: name.trim(),
      enabled,
      cron_expr: buildCron(),
      timezone: seed.timezone,
      target_mode: targetMode,
      target_ai_agent_instance_id: targetMode === 'existing' ? targetAgentID : null,
      prompt: prompt.trim(),
      // Exactly one bound is sent; the other is cleared. The server supports
      // both at once, but offering that in the picker would mean explaining
      // which one wins, for a combination nobody asks for.
      ends_at: endMode === 'date' ? endOfDayInZone(endDate, seed.timezone) : '',
      max_runs: endMode === 'count' ? endCount : 0,
    }

    setSaving(true)
    setError(null)
    const res = triggerID
      ? await updateScheduledTrigger(triggerID, input)
      : await createScheduledTrigger(orgID, input)
    setSaving(false)
    if (res.error) return setError(res.error)
    popNav()
  }

  return (
    <div className="routines">
      <NavBar
        title={triggerID ? 'Edit Routine' : 'New Routine'}
        leading={<NavBarButton icon="‹" label="Back" onClick={popNav} />}
        trailing={<NavBarButton icon={saving ? '…' : 'Save'} label="Save" onClick={save} />}
      />

      <div className="routines__scroll">
        <SectionHeader>Name</SectionHeader>
        <Group>
          <input
            className="routines__input"
            value={name}
            placeholder="Morning Sweep"
            onChange={(e) => setName(e.target.value)}
          />
        </Group>

        <SectionHeader>Schedule</SectionHeader>
        <Group>
          <Segment options={FREQ_OPTIONS} value={freq} onChange={setFreq} />
          {freq !== 'advanced' && freq !== 'hourly' && (
            <div className="routines__field">
              <span className="routines__field-label">At</span>
              <input
                type="time"
                className="routines__time"
                value={toTimeValue(hour, minute)}
                onChange={(e) => {
                  const { hour: h, minute: m } = parseTimeValue(e.target.value)
                  setHour(h)
                  setMinute(m)
                }}
              />
            </div>
          )}
          {freq === 'weekly' && (
            <div className="routines__field">
              <span className="routines__field-label">On</span>
              <select
                className="routines__select"
                value={dow}
                onChange={(e) => setDow(Number(e.target.value))}
              >
                {DOW_LABELS.map((label, i) => (
                  <option key={i} value={i}>
                    {label}
                  </option>
                ))}
              </select>
            </div>
          )}
          {freq === 'hourly' && (
            <div className="routines__field">
              <span className="routines__field-label">At minute</span>
              <input
                type="number"
                min={0}
                max={59}
                className="routines__number"
                value={minute}
                onChange={(e) => setMinute(Math.max(0, Math.min(59, Number(e.target.value) || 0)))}
              />
            </div>
          )}
          {freq === 'advanced' && (
            <input
              className="routines__input routines__input--mono"
              value={advancedCron}
              placeholder="0 5 * * *"
              onChange={(e) => setAdvancedCron(e.target.value)}
            />
          )}
        </Group>
        <p className="routines__hint">
          {preview} · Times are in {seed.timezone}.
        </p>

        <SectionHeader>Ends</SectionHeader>
        <Group>
          <Segment options={END_OPTIONS} value={endMode} onChange={(v) => {
            setEndMode(v)
            // Seed a sensible date the first time someone picks "On date",
            // rather than an empty field that fails validation on save.
            if (v === 'date' && !endDate) setEndDate(tomorrowInZone(seed.timezone))
          }} />
          {endMode === 'date' && (
            <div className="routines__field">
              <span className="routines__field-label">Last day</span>
              <input
                type="date"
                className="routines__input"
                value={endDate}
                min={tomorrowInZone(seed.timezone)}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          )}
          {endMode === 'count' && (
            <div className="routines__field">
              <span className="routines__field-label">Runs</span>
              <input
                type="number"
                min={1}
                className="routines__number"
                value={endCount}
                onChange={(e) => setEndCount(Math.max(1, Number(e.target.value) || 1))}
              />
            </div>
          )}
        </Group>
        <p className="routines__hint">{endHint}</p>

        <SectionHeader>What it does</SectionHeader>
        <Group>
          <textarea
            className="routines__textarea"
            value={prompt}
            placeholder="Check the overnight camera events and text me anything unusual, then stop."
            rows={4}
            onChange={(e) => setPrompt(e.target.value)}
          />
        </Group>

        <SectionHeader>Run with</SectionHeader>
        <Group>
          <Segment
            options={[
              { value: 'temp', label: 'Fresh agent' },
              { value: 'existing', label: 'An existing agent' },
            ]}
            value={targetMode}
            onChange={(v) => setTargetMode(v)}
          />
          {targetMode === 'existing' && (
            <div className="routines__field">
              <span className="routines__field-label">Agent</span>
              <select
                className="routines__select"
                value={targetAgentID ?? ''}
                onChange={(e) => setTargetAgentID(e.target.value || null)}
              >
                <option value="">Choose…</option>
                {fullTimeAgents.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            </div>
          )}
        </Group>
        <p className="routines__hint">
          {targetMode === 'temp'
            ? 'A throwaway agent is created for each run and cleaned up after.'
            : 'Wakes the agent if it’s asleep, then hands it this task.'}
        </p>

        <Group>
          <label className="routines__row routines__row--tappable">
            <span className="routines__row-label">Enabled</span>
            <span className="routines__toggle">
              <input type="checkbox" checked={enabled} onChange={() => setEnabled((v) => !v)} />
              <span className="routines__toggle-track" />
            </span>
          </label>
        </Group>

        {triggerID && runs.length > 0 && (
          <>
            <SectionHeader>Recent runs</SectionHeader>
            <Group>
              {runs.slice(0, 10).map((r) => (
                <div key={r.id} className="routines__run">
                  <span className={`routines__run-status routines__run-status--${runStatusTone(r.status)}`}>
                    {runStatusLabel(r.status)}
                  </span>
                  <span className="routines__run-time">{runRelativeTime(r.fired_at)}</span>
                </div>
              ))}
            </Group>
          </>
        )}

        {error && <p className="routines__error">{error}</p>}

        <button className="routines__save" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : triggerID ? 'Save Changes' : 'Create Routine'}
        </button>
      </div>
    </div>
  )
}

function SectionHeader({ children }: { children: React.ReactNode }) {
  return <div className="routines__section-header">{children}</div>
}

function Group({ children }: { children: React.ReactNode }) {
  return <div className="routines__group">{children}</div>
}

function Segment<T extends string>({
  options,
  value,
  onChange,
}: {
  options: Array<{ value: T; label: string }>
  value: T
  onChange: (v: T) => void
}) {
  return (
    <div className="routines__segment">
      {options.map((o) => (
        <button
          key={o.value}
          className={`routines__segment-btn${o.value === value ? ' routines__segment-btn--active' : ''}`}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  )
}
